/**
 * Durations are held as a number of seconds throughout the app. These convert to and from the
 * H:MM:SS text people read and type.
 *
 * Lifted out of the Toolbox mixin, which still delegates here, so callers that are not components
 * can format a duration without mixing anything in.
 */

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * 60;

/**
 * Format a duration in seconds as H:MM:SS, or MM:SS when hours are not wanted.
 *
 * Hours are not zero-padded. This text is stored on a filter's label and shown on its chip, so the
 * format has to stay put: changing it would leave filters saved before and after formatted
 * differently in the same list.
 *
 * @param {number} duration Seconds. A negative duration is rendered with a leading minus.
 * @param {boolean} [includeHours] When false, hours roll up into the minutes.
 * @returns {string} Empty string when duration is not a finite number.
 */
export function formatDuration(duration, includeHours = true) {
  if (!Number.isFinite(duration)) {
    return '';
  }

  const negativeStr = duration < 0 ? '- ' : '';
  const totalSeconds = Math.trunc(Math.abs(duration));

  const hours = includeHours ? Math.floor(totalSeconds / SECONDS_PER_HOUR) : 0;
  const minutes = Math.floor((totalSeconds - hours * SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = totalSeconds - hours * SECONDS_PER_HOUR - minutes * SECONDS_PER_MINUTE;

  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');

  return includeHours
    ? `${negativeStr}${hours}:${paddedMinutes}:${paddedSeconds}`
    : `${negativeStr}${paddedMinutes}:${paddedSeconds}`;
}

/**
 * Parse H:MM:SS back into seconds, reversing formatDuration including its leading minus.
 *
 * Anything that is not three parts is 0 rather than a partial reading, so a half-typed duration
 * cannot be mistaken for a short one. An empty component counts as half-typed: Number('') is 0, so
 * without the check "1::3" would read as an hour and three seconds rather than as nothing.
 *
 * An out-of-range component carries into the next unit instead of voiding the reading. This also
 * parses what people type behind a #:##:## mask, which accepts any digit with no rule behind it,
 * so "0:75:30" is enterable and means 75 minutes and 30 seconds rather than nothing at all.
 *
 * @param {string} hms
 * @returns {number} Seconds, negative for a duration formatted as such.
 */
export function parseDuration(hms = '') {
  const raw = String(hms ?? '').trim();
  const isNegative = raw.startsWith('-');
  const parts = (isNegative ? raw.slice(1) : raw).trim().split(':');

  /*
   * Digits only, because Number() is far more permissive than the format: it reads "1.5" as a
   * minute and a half and "1e1" as ten, neither of which formatDuration can write, and both of
   * which would come back as a duration the text never said.
   */
  if (parts.length !== 3 || parts.some((part) => !/^\d+$/.test(part.trim()))) {
    return 0;
  }

  const [hours, minutes, seconds] = parts.map(Number);

  const total = hours * SECONDS_PER_HOUR + minutes * SECONDS_PER_MINUTE + seconds;

  return isNegative ? -total : total;
}
